// Inline dismissible legend for the deliverable OccurrenceStrip's
// seven tile states. Mirrors milestones' ProgressDotsKey pattern:
// defaults DISMISSED (compact "Show occurrence strip key" affordance);
// clicking the affordance surfaces the legend; X dismisses it again.
// State persists in localStorage under
// `deliverable.occurrenceStripKey.dismissed` so the user's choice
// rides between sessions.

import { useCallback, useState } from "react";
import { HelpCircle, X } from "lucide-react";

const STORAGE_KEY = "deliverable.occurrenceStripKey.dismissed";

function readDismissed(): boolean {
  // Optional chaining covers both SSR (no window) and storage-disabled
  // contexts; the try/catch then handles `getItem` throwing in private-
  // browsing / quota-exceeded contexts. Default is dismissed — only an
  // explicit "0" surfaces the legend.
  try {
    return window?.localStorage?.getItem(STORAGE_KEY) !== "0";
  } catch {
    return true;
  }
}

function writeDismissed(dismissed: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, dismissed ? "1" : "0");
  } catch {
    // best-effort
  }
}

interface SwatchProps {
  testId: string;
  className: string;
  label: string;
  /** Full term for abbreviated labels, exposed as a hover title. */
  title?: string;
}

function Swatch({ testId, className, label, title }: SwatchProps) {
  return (
    <span className="inline-flex items-center gap-1" title={title}>
      <span
        aria-hidden="true"
        data-testid={testId}
        className={`h-2.5 w-2.5 rounded-full border ${className}`}
      />
      <span className="text-caption text-slate-600 dark:text-slate-400">
        {label}
      </span>
    </span>
  );
}

export default function OccurrenceStripKey() {
  const [dismissed, setDismissed] = useState<boolean>(readDismissed);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    writeDismissed(true);
  }, []);

  const handleShow = useCallback(() => {
    setDismissed(false);
    writeDismissed(false);
  }, []);

  if (dismissed) {
    return (
      <button
        type="button"
        onClick={handleShow}
        aria-label="Show occurrence strip key"
        data-testid="occurrence-strip-key-show"
        className="inline-flex items-center gap-1 rounded-md p-1 text-caption text-slate-500 hover:text-slate-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
        <span>Occurrence strip key</span>
      </button>
    );
  }

  return (
    <div
      data-testid="occurrence-strip-key"
      role="note"
      aria-label="Occurrence strip legend"
      className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/60"
    >
      <span className="text-caption font-medium text-slate-700 dark:text-slate-200">
        Occurrence strip:
      </span>
      <Swatch
        testId="occurrence-strip-key-swatch-accepted"
        className="border-emerald-500 bg-emerald-500"
        label="Accepted"
      />
      <Swatch
        testId="occurrence-strip-key-swatch-in-flight"
        className="border-sky-500 bg-sky-500"
        label="In flight"
      />
      <Swatch
        testId="occurrence-strip-key-swatch-attention"
        className="border-red-500 bg-transparent"
        label="Attention"
      />
      <Swatch
        testId="occurrence-strip-key-swatch-conditionally-accepted"
        className="border-amber-600 bg-amber-600"
        label="Cond. accepted"
        title="Conditionally accepted"
      />
      <Swatch
        testId="occurrence-strip-key-swatch-awaiting-resubmission"
        className="border-rose-600 bg-rose-600"
        label="Awaiting resub."
        title="Awaiting resubmission"
      />
      <Swatch
        testId="occurrence-strip-key-swatch-overdue"
        className="border-red-600 bg-red-600"
        label="Overdue"
      />
      <Swatch
        testId="occurrence-strip-key-swatch-future"
        className="border-slate-300 bg-slate-100 dark:border-slate-600 dark:bg-slate-800"
        label="Future / Not started"
      />
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss occurrence strip key"
        data-testid="occurrence-strip-key-dismiss"
        className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded-md text-slate-500 hover:text-slate-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}
